import React from "react";
import { Character, CharacterListItem } from "../../../types/types";
import { Button } from "../../ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faEye, faTrash } from "@fortawesome/free-solid-svg-icons";

export interface CharacterAction {
  label: string;
  icon: typeof faEdit | typeof faEye | typeof faTrash;
  onClick: () => void;
  variant?: "primary" | "secondary" | "danger" | "success";
  className?: string;
}

type CharacterCardCharacter = Character | CharacterListItem;

// Type guard to check if character has notes
const hasNotes = (character: CharacterCardCharacter): character is Character => {
  return "notes" in character;
};

export interface CharacterCardProps {
  character: CharacterCardCharacter;
  variant?: "default" | "compact" | "detailed";
  showActions?: boolean;
  actions?: CharacterAction[];
  onClick?: () => void;
  className?: string;
  showCampaign?: boolean;
  campaignName?: string;
}

const CharacterCard: React.FC<CharacterCardProps> = ({
  character,
  variant = "default",
  showActions = true,
  actions = [],
  onClick,
  className = "",
  showCampaign = true,
  campaignName,
}) => {
  const baseClasses =
    "bg-gray-800 border border-gray-700 rounded-lg p-4 transition-all duration-200";
  const hoverClasses = onClick ? "hover:bg-gray-700 cursor-pointer" : "";

  const variantClasses = {
    default: "space-y-3",
    compact: "space-y-2",
    detailed: "space-y-4",
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const handleActionClick = (e: React.MouseEvent, actionHandler: () => void) => {
    e.stopPropagation();
    actionHandler();
  };

  return (
    <div
      className={`${baseClasses} ${hoverClasses} ${variantClasses[variant]} ${className}`}
      onClick={handleCardClick}
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-white truncate">{character.name}</h3>
          {character.description && variant !== "compact" && (
            <p className="text-gray-300 text-sm mt-1 line-clamp-2">{character.description}</p>
          )}
        </div>

        {showActions && actions.length > 0 && (
          <div className="flex space-x-2 ml-4 flex-shrink-0">
            {actions.map((action, index) => (
              <Button
                key={index}
                variant={action.variant || "secondary"}
                size="sm"
                onClick={(e) => handleActionClick(e, action.onClick)}
                className={`p-2 ${action.className || ""}`}
                title={action.label}
              >
                <FontAwesomeIcon icon={action.icon} size="sm" />
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* Campaign Info */}
      {showCampaign && (character.campaignId || campaignName) && variant !== "compact" && (
        <div className="flex items-center text-sm">
          <span className="text-gray-400">Campaign:</span>
          <span className="text-blue-400 ml-2">
            {campaignName || `ID: ${String(character.campaignId)}`}
          </span>
        </div>
      )}

      {/* Character Stats/Details for detailed variant */}
      {variant === "detailed" && (
        <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-700">
          <div>
            <span className="text-gray-400 text-xs block">Created</span>
            <span className="text-white text-sm">
              {new Date(character.createdAt).toLocaleDateString()}
            </span>
          </div>
          {character.updatedAt && (
            <div>
              <span className="text-gray-400 text-xs block">Updated</span>
              <span className="text-white text-sm">
                {new Date(character.updatedAt).toLocaleDateString()}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Notes section for detailed variant */}
      {variant === "detailed" && hasNotes(character) && character.notes && (
        <div className="pt-2 border-t border-gray-700">
          <span className="text-gray-400 text-xs block mb-1">Notes</span>
          <p className="text-gray-300 text-sm line-clamp-3">{character.notes}</p>
        </div>
      )}

      {/* Compact additional info */}
      {variant === "compact" && showCampaign && (character.campaignId || campaignName) && (
        <div className="text-xs text-gray-400">
          Campaign: {campaignName || `ID: ${String(character.campaignId)}`}
        </div>
      )}
    </div>
  );
};

export default CharacterCard;
